import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectPaymentTypes } from "../../payment-types/payment-types-slice";
import { fetchPaymentTypes } from "../../payment-types/slice-extensions/payment-types-slice-extension";
import { selectOccurrencePaymentTypeIds } from "../visits-slice";

/**
 * Member id → the name of their Visit's own Payment Type on one occurrence (e.g. the
 * cancellation policy's late fee), so the attendance row reads "Charged · Late
 * cancellation fee". Loads the Payment Type catalog (archived included) only when needed.
 */
export const useAttendanceFeeNames = (sessionId: string, occurrenceDate: string): Record<string, string> => {
  const dispatch = useAppDispatch();
  const paymentTypeIds = useAppSelector(selectOccurrencePaymentTypeIds(sessionId, occurrenceDate));
  const paymentTypes = useAppSelector(selectPaymentTypes);
  const needed = Object.values(paymentTypeIds);
  const missing = needed.some((id) => !paymentTypes.some((type) => type.id === id));

  useEffect(() => {
    if (missing) dispatch(fetchPaymentTypes({ includeArchived: true }));
  }, [dispatch, missing]);

  return useMemo(() => {
    const names: Record<string, string> = {};
    for (const [memberId, typeId] of Object.entries(paymentTypeIds)) {
      const type = paymentTypes.find((candidate) => candidate.id === typeId);
      if (type) names[memberId] = type.name;
    }
    return names;
  }, [paymentTypeIds, paymentTypes]);
};
